<template>
  <div class="space-y-4">
    <div class="text-sm font-medium text-gray-700">
      {{ $t("DEALS.APPLICATION_LINKS.URL_PARAMETERS") }}
    </div>

    <div class="mt-2 space-y-3">
      <div
        v-for="param in parameters"
        :key="param.id"
        class="rounded-lg border border-gray-200"
      >
        <div
          class="flex cursor-pointer items-center justify-between px-4 py-3"
          @click="toggleParam(param.id)"
        >
          <span
            class="text-sm"
            :class="param.type ? 'font-medium text-gray-600' : 'text-gray-400'"
          >
            {{
              param.type
                ? getTypeLabel(param)
                : $t("DEALS.APPLICATION_LINKS.CONFIGURE_PARAMETERS")
            }}
          </span>
          <div class="flex items-center gap-2">
            <button
              v-if="parameters.length"
              v-tooltip="`${$t('COMMON.DELETE')}`"
              type="button"
              class="shrink-0 rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              @click.stop="removeParameter(param.id)"
            >
              <icon-base :icon="IconDelete" />
            </button>
            <dropdown-chevron
              :is-open="expandedParamIds.has(param.id)"
              :is-default-position="false"
            />
          </div>
        </div>

        <div v-show="expandedParamIds.has(param.id)">
          <hr class="border-gray-200" />
          <div class="flex flex-col gap-3 px-4 py-4">
            <lf-dropdown
              :options="getTypeOptions(param)"
              :value="param.type"
              :placeholder="$t('COMMON.TYPE')"
              :name="`type-${param.id}`"
              full-width
              @change="handleTypeChange(param.id, String($event))"
            />
            <div v-if="param.type === 'other'">
              <lf-input
                :placeholder="$t('DEALS.APPLICATION_LINKS.PARAMETER_KEY')"
                :value="param.customKey"
                :name="`key-${param.id}`"
                class="mb-0!"
                no-margin
                @key-released="updateParamKey(param.id, $event)"
              />
              <div v-if="getKeyError(param)" class="mt-1 text-xs text-red-500">
                {{ getKeyError(param) }}
              </div>
            </div>
            <div>
              <lf-input
                :placeholder="$t('COMMON.VALUE')"
                :value="param.value"
                :name="`value-${param.id}`"
                :maxlength="255"
                class="mb-0!"
                no-margin
                @key-released="updateParamValue(param.id, $event)"
              />
              <div
                v-if="getValueError(param)"
                class="mt-1 text-xs text-red-500"
              >
                {{ getValueError(param) }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <lf-link
      :underlined="false"
      class="py-3 flex items-center text-sm font-medium! w-fit"
      @click="addParameter"
    >
      <span class="mr-1">+</span>
      {{ $t("DEALS.APPLICATION_LINKS.ADD_URL_PARAMETER") }}
    </lf-link>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

import { v4 } from "uuid";

import { URL_PARAMETER_TYPES } from "@/helpers/constants/urlParameters";
import {
  type UrlParameter,
  urlParameterKeySchema,
} from "@/models/urlParameters";

import IconDelete from "@/components/icons/IconDelete.vue";
import DropdownChevron from "@/components/ui/DropdownChevron.vue";
import IconBase from "@/components/ui/IconBase.vue";
import LfLink from "@/components/ui/LfLink.vue";
import LfDropdown from "@/components/ui/inputs/LfDropdown.vue";
import LfInput from "@/components/ui/inputs/LfInput.vue";

const { t } = useI18n();

const parameters = defineModel<UrlParameter[]>("parameters", {
  required: true,
});

const expandedParamIds = ref<Set<string>>(
  new Set(parameters.value.map((param) => param.id))
);
const touched = ref({
  keys: new Set<string>(),
  values: new Set<string>(),
});

const toggleParam = (id: string) => {
  if (expandedParamIds.value.has(id)) {
    expandedParamIds.value.delete(id);
  } else {
    expandedParamIds.value.add(id);
  }
};

const usedTypes = computed(() =>
  parameters.value
    .filter((param) => param.type && param.type !== "other")
    .map((param) => param.type)
);

const getTypeLabel = (param: UrlParameter) => {
  if (param.type === "other") {
    return param.customKey || "Other";
  }
  const found = URL_PARAMETER_TYPES.find(
    (option) => option.value === param.type
  );
  return found?.label ?? param.type;
};

const getTypeOptions = (currentParam: UrlParameter) => {
  const options: Record<string, string> = {};
  for (const paramType of URL_PARAMETER_TYPES) {
    if (
      paramType.value === currentParam.type ||
      !usedTypes.value.includes(paramType.value)
    ) {
      options[paramType.value] = paramType.label;
    }
  }
  return options;
};

const allUsedKeys = computed(() =>
  parameters.value.reduce<Record<string, number>>((keys, param) => {
    const key = param.type === "other" ? param.customKey.trim() : param.type;
    if (key) {
      keys[key] = (keys[key] || 0) + 1;
    }
    return keys;
  }, {})
);

const isDuplicateKey = (param: UrlParameter): boolean => {
  const key = param.type === "other" ? param.customKey.trim() : param.type;
  if (!key) {
    return false;
  }
  return (allUsedKeys.value[key] || 0) > 1;
};

const getKeyError = (param: UrlParameter): string | null => {
  if (!touched.value.keys.has(param.id) || !param.customKey) {
    return null;
  }

  const result = urlParameterKeySchema.safeParse(param.customKey);
  if (!result.success) {
    return (
      result.error.issues[0]?.message ??
      t("DEALS.APPLICATION_LINKS.INVALID_KEY")
    );
  }

  if (isDuplicateKey(param)) {
    return t("DEALS.APPLICATION_LINKS.DUPLICATE_KEY");
  }

  return null;
};

const getValueError = (param: UrlParameter): string | null => {
  if (
    !touched.value.values.has(param.id) ||
    !param.type ||
    param.value.trim()
  ) {
    return null;
  }
  return t("DEALS.APPLICATION_LINKS.VALUE_REQUIRED");
};

const hasValidationErrors = computed(() =>
  parameters.value.some((param) => {
    if (param.type === "other" && param.customKey) {
      if (!urlParameterKeySchema.safeParse(param.customKey).success) {
        return true;
      }
    }
    if (param.type && !param.value.trim()) {
      return true;
    }
    if (isDuplicateKey(param)) {
      return true;
    }
    return false;
  })
);

const handleTypeChange = (id: string, value: string) => {
  const param = parameters.value.find((entry) => entry.id === id);
  if (!param) {
    return;
  }

  param.type = value;
  if (value !== "other") {
    param.customKey = "";
  }
};

const updateParamKey = (id: string, value: string) => {
  const param = parameters.value.find((entry) => entry.id === id);
  if (param) {
    param.customKey = value;
    touched.value.keys.add(id);
  }
};

const updateParamValue = (id: string, value: string) => {
  const param = parameters.value.find((entry) => entry.id === id);
  if (param) {
    param.value = value;
    touched.value.values.add(id);
  }
};

const addParameter = () => {
  const id = v4();
  parameters.value.push({
    id,
    type: "",
    customKey: "",
    value: "",
  });
  expandedParamIds.value.add(id);
};

const removeParameter = (id: string) => {
  const index = parameters.value.findIndex((entry) => entry.id === id);
  if (index !== -1) {
    parameters.value.splice(index, 1);
    expandedParamIds.value.delete(id);
    touched.value.keys.delete(id);
    touched.value.values.delete(id);
  }
};

defineExpose({ hasValidationErrors });
</script>
